import React, { useEffect, useState } from 'react';
import { Share2, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../apiClient';
import { ProjectCard, FolderCard } from './ProjectsPage';
import VisibilityIndicator from '../components/VisibilityIndicator';
import { richTextToPlainText } from '../utils/richText';

function SharedPage() {
  const [projects, setProjects] = useState([]);
  const [folders, setFolders] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [projectData, folderData, promptData] = await Promise.all([
          apiClient.get('/api/shared/projects'),
          apiClient.get('/api/shared/folders'),
          apiClient.get('/api/shared/prompts')
        ]);
        if (!mounted) return;
        setProjects(projectData || []);
        setFolders(folderData || []);
        setPrompts(promptData || []);
      } catch (err) {
        if (!mounted) return;
        setError('Unable to load shared items.');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  const hasItems = projects.length || folders.length || prompts.length;

  return (
    <div className="min-h-screen projects-page">
      <div className="page-header">
        <div className="page-header-top">
          <div>
            <h1 className="text-3xl font-bold mb-2">Shared</h1>
            <p className="text-gray-400">
              Projects, folders, and prompts shared with you will appear here.
            </p>
          </div>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {loading ? (
        <div className="spinner"></div>
      ) : (
        <>
          {!hasItems && (
            <div className="card-glass empty-state py-12">
              <div className="mx-auto w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mb-4">
                <Share2 className="w-6 h-6 text-rebel-blue" />
              </div>
              <h3 className="text-xl font-bold mb-2">Nothing shared yet</h3>
              <p className="text-gray-400">
                When someone shares a project, folder, or prompt, it will show up here.
              </p>
            </div>
          )}

          {projects.length > 0 && (
            <div className="shared-section">
              <div className="section-header">
                <h2 className="text-xl font-bold">Projects</h2>
                <span className="text-sm text-gray-400">{projects.length} shared</span>
              </div>
              <div className="tile-grid">
                {projects.map((project) => (
                  <ProjectCard
                    key={project.project_id}
                    project={project}
                    pinned={false}
                    showActions={false}
                    onOpen={() => navigate(`/projects/${project.project_id}`)}
                  />
                ))}
              </div>
            </div>
          )}

          {folders.length > 0 && (
            <div className="shared-section">
              <div className="section-header">
                <h2 className="text-xl font-bold">Folders</h2>
                <span className="text-sm text-gray-400">{folders.length} shared</span>
              </div>
              <div className="tile-grid">
                {folders.map((folder) => (
                  <FolderCard
                    key={folder.folder_id}
                    folder={folder}
                    pinned={false}
                    showActions={false}
                    onOpen={() => {
                      const projectId = folder.project_id;
                      if (projectId) {
                        navigate(`/projects/${projectId}/folders/${folder.folder_id}`);
                      } else {
                        navigate('/projects');
                      }
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {prompts.length > 0 && (
            <div className="shared-section">
              <div className="section-header">
                <h2 className="text-xl font-bold">Prompts</h2>
                <span className="text-sm text-gray-400">{prompts.length} shared</span>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {prompts.map((prompt) => (
                  <div
                    key={prompt.prompt_id}
                    className="card-glass cursor-pointer"
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/prompts/${prompt.prompt_id}`, {
                      state: {
                        backTo: '/shared',
                        breadcrumbs: [{ label: 'Shared', to: '/shared' }]
                      }
                    })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(`/prompts/${prompt.prompt_id}`, {
                          state: {
                            backTo: '/shared',
                            breadcrumbs: [{ label: 'Shared', to: '/shared' }]
                          }
                        });
                      }
                    }}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="font-bold text-lg">{prompt.name}</h3>
                      <div className="flex items-center gap-2">
                        <VisibilityIndicator visibility={prompt.visibility || 'private'} />
                        <span className="badge">{prompt.visibility || 'private'}</span>
                      </div>
                    </div>
                    <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                      {richTextToPlainText(prompt.description) || 'No description'}
                    </p>
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1">
                          <Star className="w-4 h-4" />
                          {Number(prompt.average_rating ?? 0).toFixed(1)}
                        </span>
                        <span>{prompt.view_count ?? 0} views</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default SharedPage;
